import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import GaugeComponent from 'react-gauge-component';
import * as XLSX from 'xlsx';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, ComposedChart
} from 'recharts';
import { 
  ShoppingCart, Search, Filter, RefreshCw, Eye, Trash2,
  Clock, CheckCircle, XCircle, Truck, Package,
  AlertCircle, DollarSign, Calendar, User,
  MapPin, Phone, Mail, CreditCard, X, TrendingUp, Activity, Download,
  AlertTriangle, Edit, Save, ChevronDown
} from 'lucide-react';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const AdminOrderDashboard = () => {
  // User role state
  const [userRole, setUserRole] = useState(null);
  const isOwner = userRole === 'owner';
  
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // BidVest Transaction Tracker — see what the GATEWAY says, not what we think.
  const [bidvestCheck, setBidvestCheck] = useState(null);
  const [bidvestChecking, setBidvestChecking] = useState(false);
  // order_number -> { gavePage, ourError } from checkout_attempts
  const [checkoutAttempts, setCheckoutAttempts] = useState({});
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    picking: 0,
    picking_complete: 0,
    packing: 0,
    packed: 0,
    dispatched: 0,
    completed: 0,
    cancelled: 0
  });

  // New analytics state
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    avgOrderValue: 0,
    completionRate: 0,
    avgProcessingTime: 0,
    ordersPerHour: 0,
    fulfillmentRate: 0
  });
  const [revenueByStatus, setRevenueByStatus] = useState([]);
  const [hourlyOrders, setHourlyOrders] = useState([]);
  const [orderValueDistribution, setOrderValueDistribution] = useState([]);
  const [processingTimeTrends, setProcessingTimeTrends] = useState([]);
  const [paymentMethodBreakdown, setPaymentMethodBreakdown] = useState([]);
  
  // Stuck orders state
  const [stuckOrders, setStuckOrders] = useState([]);
  const [stuckOrdersLoading, setStuckOrdersLoading] = useState(false);
  
  // Status update state
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState(null);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(null);

  // Available statuses for admin to update
  const ORDER_STATUSES = [
    { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-700' },
    { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
    { value: 'picking', label: 'Picking', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'picking_complete', label: 'Picking Complete', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'packing', label: 'Packing', color: 'bg-orange-100 text-orange-700' },
    { value: 'packed', label: 'Packed', color: 'bg-orange-100 text-orange-700' },
    { value: 'ready_for_dispatch', label: 'Ready for Dispatch', color: 'bg-purple-100 text-purple-700' },
    { value: 'out_for_delivery', label: 'Out for Delivery', color: 'bg-indigo-100 text-indigo-700' },
    { value: 'delivered', label: 'Delivered', color: 'bg-green-100 text-green-700' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' }
  ];

  const PAYMENT_STATUSES = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700' },
    { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-700' },
    { value: 'refunded', label: 'Refunded', color: 'bg-gray-100 text-gray-700' }
  ];

  useEffect(() => {
    fetchUserRole();
    loadOrders();
    loadStuckOrders();
    
    // Refresh every 3 minutes
    const interval = setInterval(loadOrders, 180000);
    const stuckInterval = setInterval(loadStuckOrders, 120000); // Every 2 minutes
    return () => {
      clearInterval(interval);
      clearInterval(stuckInterval);
    };
  }, []);

  // Fetch current user's role
  const fetchUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (!error && profile) {
          setUserRole(profile.role);
          console.log('User role:', profile.role);
        }
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  useEffect(() => {
    filterOrders();
    if (orders.length > 0) {
      calculateAdvancedMetrics();
    }
  }, [orders, searchTerm, statusFilter, dateFilter]);

  const getDateRange = () => {
    const end = new Date();
    let start = new Date();
    
    switch(dateFilter) {
      case 'today':
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start.setDate(start.getDate() - 7);
        break;
      case 'month':
        start.setDate(start.getDate() - 30);
        break;
      default:
        start.setHours(0, 0, 0, 0);
    }
    
    return { start, end };
  };

  const calculateAdvancedMetrics = () => {
    const { start, end } = getDateRange();
    
    // Filter orders by date range
    const rangeOrders = orders.filter(order => {
      const orderDate = new Date(order.created_at);
      return orderDate >= start && orderDate <= end;
    });

    if (rangeOrders.length === 0) {
      setMetrics({
        totalRevenue: 0,
        avgOrderValue: 0,
        completionRate: 0,
        avgProcessingTime: 0,
        ordersPerHour: 0,
        fulfillmentRate: 0
      });
      return;
    }

    // Total Revenue - only from delivered orders
    const deliveredOrders = rangeOrders.filter(o => 
      o.status?.toLowerCase() === 'delivered'
    );
    const totalRevenue = deliveredOrders.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0);
    
    // Average Order Value - based on delivered orders
    const avgOrderValue = deliveredOrders.length > 0 ? totalRevenue / deliveredOrders.length : 0;
    
    // Completion Rate
    const completedOrders = rangeOrders.filter(o => 
      ['completed', 'delivered'].includes(o.status?.toLowerCase())
    ).length;
    const completionRate = (completedOrders / rangeOrders.length) * 100;
    
    // Fulfillment Rate (orders that made it past confirmed stage)
    const fulfilledOrders = rangeOrders.filter(o => 
      !['pending', 'cancelled'].includes(o.status?.toLowerCase())
    ).length;
    const fulfillmentRate = (fulfilledOrders / rangeOrders.length) * 100;
    
    // Average Processing Time
    const ordersWithTimes = rangeOrders.filter(o => 
      o.created_at && o.delivered_at
    );
    let avgProcessingTime = 0;
    if (ordersWithTimes.length > 0) {
      const totalTime = ordersWithTimes.reduce((sum, o) => {
        const time = (new Date(o.delivered_at) - new Date(o.created_at)) / (1000 * 60 * 60);
        return sum + time;
      }, 0);
      avgProcessingTime = totalTime / ordersWithTimes.length;
    }
    
    // Orders Per Hour
    const hoursInRange = (end - start) / (1000 * 60 * 60);
    const ordersPerHour = hoursInRange > 0 ? (rangeOrders.length / hoursInRange) : 0;

    setMetrics({
      totalRevenue,
      avgOrderValue,
      completionRate,
      avgProcessingTime,
      ordersPerHour,
      fulfillmentRate
    });

    // Calculate revenue by status
    const statusRevenue = {};
    rangeOrders.forEach(order => {
      const status = order.status || 'pending';
      if (!statusRevenue[status]) {
        statusRevenue[status] = 0;
      }
      statusRevenue[status] += parseFloat(order.total) || 0;
    });
    
    const revenueByStatusArray = Object.entries(statusRevenue).map(([status, revenue]) => ({
      status: status.charAt(0).toUpperCase() + status.slice(1),
      revenue
    })).sort((a, b) => b.revenue - a.revenue);
    
    setRevenueByStatus(revenueByStatusArray);

    // Hourly orders
    const hourlyData = {};
    rangeOrders.forEach(order => {
      const hour = new Date(order.created_at).getHours();
      const hourKey = `${hour.toString().padStart(2, '0')}:00`;
      
      if (!hourlyData[hourKey]) {
        hourlyData[hourKey] = {
          hour: hourKey,
          orders: 0,
          revenue: 0
        };
      }
      
      hourlyData[hourKey].orders += 1;
      hourlyData[hourKey].revenue += parseFloat(order.total) || 0;
    });

    const hourlyArray = Object.values(hourlyData).sort((a, b) => 
      a.hour.localeCompare(b.hour)
    );
    setHourlyOrders(hourlyArray);

    // Order value distribution
    const distribution = {
      'R0-100': 0,
      'R100-250': 0,
      'R250-500': 0,
      'R500-1000': 0,
      'R1000+': 0
    };

    rangeOrders.forEach(order => {
      const total = parseFloat(order.total) || 0;
      if (total < 100) distribution['R0-100'] += 1;
      else if (total < 250) distribution['R100-250'] += 1;
      else if (total < 500) distribution['R250-500'] += 1;
      else if (total < 1000) distribution['R500-1000'] += 1;
      else distribution['R1000+'] += 1;
    });

    const distributionArray = Object.entries(distribution).map(([range, count]) => ({
      range,
      count
    }));
    setOrderValueDistribution(distributionArray);

    // Payment method breakdown
    const paymentMethods = {};
    rangeOrders.forEach(order => {
      const method = order.payment_method || 'Unknown';
      paymentMethods[method] = (paymentMethods[method] || 0) + 1;
    });

    const paymentArray = Object.entries(paymentMethods).map(([method, count]) => ({
      method: method.charAt(0).toUpperCase() + method.slice(1),
      count
    }));
    setPaymentMethodBreakdown(paymentArray);

    // Processing time trends (by day for week/month view)
    if (dateFilter !== 'today') {
      const dailyProcessing = {};
      
      ordersWithTimes.forEach(order => {
        const date = new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const processingTime = (new Date(order.delivered_at) - new Date(order.created_at)) / (1000 * 60 * 60);
        
        if (!dailyProcessing[date]) {
          dailyProcessing[date] = {
            date,
            totalTime: 0,
            count: 0
          };
        }
        
        dailyProcessing[date].totalTime += processingTime;
        dailyProcessing[date].count += 1;
      });

      const trendsArray = Object.values(dailyProcessing).map(d => ({
        date: d.date,
        avgTime: d.count > 0 ? d.totalTime / d.count : 0
      }));
      
      setProcessingTimeTrends(trendsArray);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            quantity,
            price,
            promo_price,
            picked_quantity,
            promotion_type,
            subtotal,
            products (
              name,
              image
            )
          )
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      if (ordersError) {
        console.error('Error loading orders:', ordersError);
        alert('Error loading orders: ' + ordersError.message);
        setLoading(false);
        return;
      }

      if (!ordersData || ordersData.length === 0) {
        console.log('No orders found in database');
        setOrders([]);
        calculateStats([]);
        setLoading(false);
        return;
      }

      // Did we ever actually hand this customer a payment page? One query for the
      // whole page of orders. Without it the payment column can only guess from the
      // order's age, which is how "declined by the bank" and "wandered off" both
      // ended up labelled ABANDONED.
      const orderNumbers = ordersData.map(o => o.order_number).filter(Boolean);
      const attemptMap = {};
      for (let i = 0; i < orderNumbers.length; i += 200) {
        const { data: attempts } = await supabase
          .from('checkout_attempts')
          .select('order_number, outcome, created_at')
          .in('order_number', orderNumbers.slice(i, i + 200));
        attempts?.forEach(a => {
          const cur = attemptMap[a.order_number] || { gavePage: false, ourError: null };
          if (a.outcome === 'page_created') cur.gavePage = true;
          else cur.ourError = a.outcome;
          attemptMap[a.order_number] = cur;
        });
      }
      setCheckoutAttempts(attemptMap);

      const userIds = [...new Set(ordersData.map(o => o.user_id).filter(Boolean))];
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, name, email, phone')
        .in('id', userIds);

      const profileMap = {};
      profiles?.forEach(p => {
        profileMap[p.id] = p;
      });

      const addressIds = [...new Set(ordersData.map(o => o.delivery_address_id).filter(Boolean))];
      const { data: addresses } = await supabase
        .from('addresses')
        .select('*')
        .in('id', addressIds);

      const addressMap = {};
      addresses?.forEach(a => {
        addressMap[a.id] = a;
      });

      const enrichedOrders = ordersData.map(order => ({
        ...order,
        user_profiles: profileMap[order.user_id] || null,
        delivery_addresses: order.delivery_address_snapshot || addressMap[order.delivery_address_id] || null
      }));

      console.log(`Loaded ${enrichedOrders.length} orders`);
      setOrders(enrichedOrders);
      calculateStats(enrichedOrders);
    } catch (error) {
      console.error('Unexpected error loading orders:', error);
    }
    
    setLoading(false);
  };

  // ============================================
  // 🚨 STUCK ORDERS - Orders where payment was never initiated
  // ============================================
  // ============================================
  // 🔍 BIDVEST TRANSACTION TRACKER
  // Asks BidVest what ACTUALLY happened to this order, on demand, read-only.
  // Exists because our payment_status is only ever a claim: the webhook can be down
  // (it returned 401 to BidVest for over a week), the browser can lie about a
  // redirect, and an admin can mark an order paid by hand. This is the only view
  // of the gateway's own truth.
  // ============================================
  const checkBidvest = async (order) => {
    if (!order?.order_number) return;
    setBidvestChecking(true);
    setBidvestCheck(null);
    try {
      const { data, error } = await supabase.functions.invoke('bidvest-reconcile', {
        body: { order_number: order.order_number, probe: true }
      });
      if (error) throw error;
      setBidvestCheck(data);
    } catch (e) {
      setBidvestCheck({ error: e?.message || 'Could not reach BidVest' });
    } finally {
      setBidvestChecking(false);
    }
  };

  // Never show one order's gateway result against another order.
  useEffect(() => {
    setBidvestCheck(null);
    setBidvestChecking(false);
  }, [selectedOrder?.id]);

  // ============================================
  // 🔄 UPDATE ORDER STATUS
  // ============================================
  const updateOrderStatus = async (orderId, newStatus, newPaymentStatus = null) => {
    setUpdatingStatus(true);
    setStatusUpdateError(null);
    setStatusUpdateSuccess(null);

    try {
      // 🛡️ NEVER mark an order paid without asking the gateway first.
      // Marking paid by hand is how ORD-20260716-5350 was confirmed and DELIVERED
      // while BidVest had no transaction for it at all — the goods went out for free.
      // Ask BidVest: if the money is really there it confirms the order itself and we
      // don't need to write anything. If it isn't, the operator must say so explicitly.
      if (newPaymentStatus === 'paid' && selectedOrder?.payment_status !== 'paid') {
        const orderNumber = selectedOrder?.order_number;
        let verdict = null;
        try {
          const { data } = await supabase.functions.invoke('bidvest-reconcile', {
            body: { order_number: orderNumber }
          });
          verdict = data;
        } catch (e) {
          console.warn('Could not reach BidVest to verify:', e);
        }

        if (verdict?.outcome !== 'paid') {
          const reason = verdict?.outcome === 'declined'
            ? `BidVest DECLINED this payment (${verdict?.decline_reason || 'no reason given'}).`
            : verdict?.outcome === 'no_attempt'
              ? 'BidVest has NO transaction for this order — the customer never paid.'
              : `BidVest could not confirm payment (${verdict?.outcome || 'no response'}).`;
          const ok = window.confirm(
            `⚠️ ${reason}\n\nOrder ${orderNumber} — R${selectedOrder?.total}\n\n` +
            `Marking it paid will send it to picking and it may be delivered with no money behind it.\n\n` +
            `Only continue if you have received payment another way (cash/EFT) and can account for it.\n\n` +
            `Mark as paid anyway?`
          );
          if (!ok) {
            setUpdatingStatus(false);
            setStatusUpdateError('Cancelled — BidVest did not confirm payment for this order.');
            return;
          }
        }
      }

      // Build update object
      const updates = { status: newStatus };

      // If payment status is provided, update it too
      if (newPaymentStatus) {
        updates.payment_status = newPaymentStatus;
        if (newPaymentStatus === 'paid' && !selectedOrder?.paid_at) {
          updates.paid_at = new Date().toISOString();
        }
      }

      // Update the order
      const { error: updateError } = await supabase
        .from('orders')
        .update(updates)
        .eq('id', orderId);

      if (updateError) {
        throw updateError;
      }

      // NO explicit order_status_history insert here — the trigger on orders
      // already writes one for every status change (that is how picker/packer
      // statuses get their history without any app inserting them). Writing a
      // second row fired the notification webhook TWICE, which is why customers
      // got duplicate "Order Confirmed" pushes a second apart, on admin-driven
      // statuses only.

      // Update local state
      setSelectedOrder(prev => ({
        ...prev,
        status: newStatus,
        ...(newPaymentStatus && { payment_status: newPaymentStatus }),
        ...(newPaymentStatus === 'paid' && !prev?.paid_at && { paid_at: new Date().toISOString() })
      }));

      // Update orders list
      setOrders(prev => prev.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              status: newStatus,
              ...(newPaymentStatus && { payment_status: newPaymentStatus }),
              ...(newPaymentStatus === 'paid' && !order.paid_at && { paid_at: new Date().toISOString() })
            }
          : order
      ));

      setStatusUpdateSuccess(`Status updated to ${newStatus}`);
      setTimeout(() => setStatusUpdateSuccess(null), 3000);

    } catch (error) {
      console.error('Error updating order status:', error);
      setStatusUpdateError(error.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const loadStuckOrders = async () => {
    try {
      setStuckOrdersLoading(true);
      
      // Fetch orders that are stuck:
      // - Status is pending
      // - Payment status is pending  
      // - No payment_key (never reached payment gateway)
      // - Created more than 10 minutes ago
      // - Within last 24 hours
      const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
      const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          total,
          created_at,
          payment_key,
          payment_method,
          user_id
        `)
        .eq('status', 'pending')
        .eq('payment_status', 'pending')
        .is('payment_key', null)
        .lt('created_at', tenMinutesAgo)
        .gt('created_at', twentyFourHoursAgo)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading stuck orders:', error);
        return;
      }

      // Fetch user profiles for stuck orders
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(o => o.user_id).filter(Boolean))];
        const { data: profiles } = await supabase
          .from('user_profiles')
          .select('id, name, email')
          .in('id', userIds);

        const profileMap = {};
        profiles?.forEach(p => { profileMap[p.id] = p; });

        const enrichedStuckOrders = data.map(order => ({
          ...order,
          user_profile: profileMap[order.user_id] || null
        }));

        setStuckOrders(enrichedStuckOrders);
      } else {
        setStuckOrders([]);
      }
    } catch (err) {
      console.error('Error in loadStuckOrders:', err);
    } finally {
      setStuckOrdersLoading(false);
    }
  };

  const cancelStuckOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Are you sure you want to cancel order ${orderNumber}?`)) {
      return;
    }
    
    try {
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          status: 'cancelled',
          payment_status: 'expired',
          updated_at: new Date().toISOString()
        })
        .eq('id', orderId);

      if (updateError) throw updateError;

      // Add to order history
      await supabase.from('order_status_history').insert({
        order_id: orderId,
        status: 'cancelled',
        notes: 'Cancelled by admin - payment never initiated',
        created_at: new Date().toISOString()
      });

      // Refresh the lists
      loadStuckOrders();
      loadOrders();
      alert(`Order ${orderNumber} has been cancelled.`);
    } catch (err) {
      console.error('Error cancelling stuck order:', err);
      alert(`Failed to cancel order: ${err.message}`);
    }
  };

  const getMinutesAgo = (createdAt) => {
    const minutes = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m ago`;
  };

  const calculateStats = (ordersData) => {
    const stats = {
      total: ordersData.length,
      pending: 0,
      confirmed: 0,
      picking: 0,
      picking_complete: 0,
      packing: 0,
      packed: 0,
      dispatched: 0,
      completed: 0,
      cancelled: 0
    };

    ordersData.forEach(order => {
      const status = order.status?.toLowerCase() || 'pending';
      stats[status] = (stats[status] || 0) + 1;
    });

    setStats(stats);
  };

  const filterOrders = () => {
    let filtered = [...orders];

    // Date filter first
    if (dateFilter !== 'all') {
      const { start } = getDateRange();
      filtered = filtered.filter(order => 
        new Date(order.created_at) >= start
      );
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.user_profiles?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.user_profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter === 'expired') {
      // Explicit view of the dead/abandoned attempts (auto-cancelled or cancelled-never-paid).
      filtered = filtered.filter(order => {
        const ps = order.payment_status?.toLowerCase();
        const st = order.status?.toLowerCase();
        return ps === 'expired' || (st === 'cancelled' && ps !== 'paid');
      });
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter(order =>
        order.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    } else {
      // Default "All" view hides dead abandoned attempts (auto-cancelled/expired, or
      // cancelled while never paid) so the dashboard shows real orders. They're kept in the
      // DB — pick "Cancelled" or "Expired / Abandoned" to see them.
      filtered = filtered.filter(order => {
        const ps = order.payment_status?.toLowerCase();
        const st = order.status?.toLowerCase();
        const deadAbandoned = ps === 'expired' || (st === 'cancelled' && ps !== 'paid');
        return !deadAbandoned;
      });
    }

    setFilteredOrders(filtered);
  };

  const handleViewDetails = async (order) => {
    try {
      // Fetch basic order data with items
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (
            id,
            quantity,
            price,
            promo_price,
            picked_quantity,
            promotion_type,
            subtotal,
            products (
              id,
              name,
              image,
              barcode
            )
          )
        `)
        .eq('id', order.id)
        .single();

      if (orderError) {
        console.error('Error loading order details:', orderError);
        alert('Error loading order details');
        return;
      }

      // Fetch user profile
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', orderData.user_id)
        .maybeSingle();

      // Fetch delivery address
      const { data: address } = await supabase
        .from('delivery_addresses')
        .select('*')
        .eq('id', orderData.delivery_address_id)
        .maybeSingle();

      // Fetch order status history for ALL timestamps
      const { data: statusHistory } = await supabase
        .from('order_status_history')
        .select('status, created_at')
        .eq('order_id', order.id)
        .order('created_at', { ascending: true });

      // Build status timestamps map
      const statusTimestamps = {};
      statusHistory?.forEach(sh => {
        statusTimestamps[sh.status] = sh.created_at;
      });

      // Fetch box allocation if exists
      let boxAllocation = null;
      if (orderData.box_allocation_id) {
        const { data: boxData } = await supabase
          .from('box_allocations')
          .select(`
            id,
            allocated_at,
            delivery_boxes (
              box_number,
              current_zone
            )
          `)
          .eq('id', orderData.box_allocation_id)
          .maybeSingle();
        boxAllocation = boxData;
      }

      // Fetch driver assignment and driver details
      let driverInfo = null;
      const { data: driverAssignment } = await supabase
        .from('driver_assignments')
        .select('*')
        .eq('order_id', order.id)
        .maybeSingle();

      if (driverAssignment?.driver_id) {
        // Try drivers table first, then user_profiles
        const { data: driver } = await supabase
          .from('drivers')
          .select('id, name, phone')
          .eq('id', driverAssignment.driver_id)
          .maybeSingle();
        
        if (driver) {
          driverInfo = driver;
        } else {
          const { data: userProfile } = await supabase
            .from('user_profiles')
            .select('id, name, phone')
            .eq('id', driverAssignment.driver_id)
            .maybeSingle();
          driverInfo = userProfile;
        }
      }

      setSelectedOrder({
        ...orderData,
        user_profiles: profile,
        delivery_addresses: orderData.delivery_address_snapshot || address,
        status_history: statusTimestamps,
        box_allocation: boxAllocation,
        driver: driverInfo
      });
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error loading order details:', error);
      alert('Error loading order details: ' + error.message);
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    setDeleting(true);
    try {
      // Delete order items first (foreign key constraint)
      const { error: itemsError } = await supabase
        .from('order_items')
        .delete()
        .eq('order_id', orderToDelete.id);

      if (itemsError) {
        console.error('Error deleting order items:', itemsError);
        alert('Error deleting order items: ' + itemsError.message);
        setDeleting(false);
        return;
      }

      // Delete any status history
      await supabase
        .from('order_status_history')
        .delete()
        .eq('order_id', orderToDelete.id);

      // Delete the order
      const { error: orderError } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderToDelete.id);

      if (orderError) {
        console.error('Error deleting order:', orderError);
        alert('Error deleting order: ' + orderError.message);
        setDeleting(false);
        return;
      }

      // Remove from local state
      setOrders(prev => prev.filter(o => o.id !== orderToDelete.id));
      setFilteredOrders(prev => prev.filter(o => o.id !== orderToDelete.id));
      
      setShowDeleteModal(false);
      setOrderToDelete(null);
      alert('Order deleted successfully');
    } catch (error) {
      console.error('Error deleting order:', error);
      alert('Error deleting order: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-700',
      confirmed: 'bg-blue-100 text-blue-700',
      picking: 'bg-yellow-100 text-yellow-700',
      picking_complete: 'bg-purple-100 text-purple-700',
      packing: 'bg-orange-100 text-orange-700',
      packed: 'bg-indigo-100 text-indigo-700',
      dispatched: 'bg-teal-100 text-teal-700',
      completed: 'bg-green-100 text-green-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  // What is ACTUALLY going on with this order's payment.
  //
  // This used to guess from the order's AGE: under 10 min = "paying now", under 2h =
  // "not completed", older = "abandoned". So a card the bank declined and a customer
  // who wandered off both read ABANDONED, and the column told you nothing. It also
  // leaned on transaction_id/gateway_reference — the exact fields the webhook stopped
  // writing while it was returning 401 to BidVest, so that branch never fired either.
  //
  // Now it reports facts:
  //   payment_status            — what BidVest told us (the webhook works again)
  //   payment_failure_reason    — the bank's own words
  //   checkout_attempts         — whether we ever handed them a payment page
  //
  // A pending order can only legitimately be DECLINED or WALKED AWAY. Anything else
  // is our fault and says so.
  const getPaymentState = (order) => {
    const ps = (order.payment_status || '').toLowerCase();
    // NOTE: `label` must stay SHORT — it renders inside a nowrap badge in a table cell.
    // Anything long belongs in `reason`, which renders stacked underneath and wraps.
    // Putting the bank's reason in the label stretched the column and pushed the
    // Actions column off screen.
    if (ps === 'paid') return { label: 'PAID', color: 'bg-green-100 text-green-800' };
    if (ps === 'failed') {
      return {
        label: 'DECLINED',
        reason: order.payment_failure_reason || null,
        color: 'bg-red-100 text-red-800',
      };
    }
    if (ps === 'cancelled' || ps === 'expired') return { label: ps.toUpperCase(), color: 'bg-gray-100 text-gray-600' };
    if (['refunded', 'partially_refunded'].includes(ps)) return { label: ps.replace('_', ' ').toUpperCase(), color: 'bg-purple-100 text-purple-700' };

    // ---- pending ----
    const att = checkoutAttempts[order.order_number];
    const ageMin = (Date.now() - new Date(order.created_at).getTime()) / 60000;

    // Money is in and we have not released it. The one that costs a customer.
    if (order.transaction_id || order.gateway_reference || order.payment_key) {
      return { label: '🚨 PAID', reason: 'not released — release it', color: 'bg-red-100 text-red-800' };
    }
    // Genuinely mid-payment. Age is a fair signal for this one case only.
    if (ageMin < 10) return { label: '🕐 PAYING NOW', color: 'bg-blue-100 text-blue-700' };
    // We failed to open a payment page. Our lost sale, not their choice.
    if (att?.ourError) return { label: '🚨 NO PAGE', reason: 'checkout failed — our error', color: 'bg-red-100 text-red-800' };
    // We gave them a page and they did not pay. A real customer decision.
    if (att?.gavePage) return { label: '💤 WALKED AWAY', color: 'bg-gray-100 text-gray-600' };
    // No checkout log at all: either pre-2026-07-17 or we never called the gateway.
    // Never claim to know which — that guess is what this whole change removes.
    return { label: '❔ UNKNOWN', reason: 'no checkout log — check BidVest', color: 'bg-amber-100 text-amber-800' };
  };

  const formatCurrency = (amount) => {
    return `R ${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToExcel = () => {
    const exportData = filteredOrders.map(order => ({
      'Order #': order.order_number,
      'Customer': order.user_profiles?.name || 'Unknown',
      'Email': order.user_profiles?.email || '',
      'Phone': order.user_profiles?.phone || '',
      'Items': order.order_items?.length || 0,
      'Subtotal': parseFloat(order.subtotal || 0).toFixed(2),
      'Delivery Fee': parseFloat(order.delivery_fee || 0).toFixed(2),
      'Total': parseFloat(order.total || 0).toFixed(2),
      'Status': order.status?.toUpperCase() || '',
      'Payment Status': order.payment_status?.toUpperCase() || '',
      'Payment Method': order.payment_method || '',
      'Created At': formatDate(order.created_at),
      'Delivered At': order.delivered_at ? formatDate(order.delivered_at) : ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    
    const fileName = `orders_${statusFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <ShoppingCart className="text-blue-600" size={36} />
                Order Management Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Real-time order tracking and analytics</p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
              <button
                onClick={loadOrders}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard 
            icon={<ShoppingCart size={24} />}
            label="Total Orders"
            value={filteredOrders.length}
            color="blue"
          />
          <StatCard 
            icon={<DollarSign size={24} />}
            label="Total Revenue"
            value={formatCurrency(metrics.totalRevenue)}
            color="green"
          />
          <StatCard 
            icon={<TrendingUp size={24} />}
            label="Avg Order Value"
            value={formatCurrency(metrics.avgOrderValue)}
            color="purple"
          />
          <StatCard 
            icon={<CheckCircle size={24} />}
            label="Completion Rate"
            value={`${metrics.completionRate.toFixed(1)}%`}
            color="green"
          />
        </div>

        {/* 🚨 Stuck Orders Alert Widget */}
        {stuckOrders.length > 0 && (
          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="text-amber-600" size={24} />
                <h3 className="text-lg font-semibold text-amber-800">
                  Stuck Orders ({stuckOrders.length})
                </h3>
              </div>
              <button
                onClick={loadStuckOrders}
                disabled={stuckOrdersLoading}
                className="flex items-center gap-2 px-3 py-1 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors"
              >
                <RefreshCw size={14} className={stuckOrdersLoading ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>
            <p className="text-sm text-amber-700 mb-4">
              These orders are stuck - payment was never initiated (no payment_key). Customer may have abandoned checkout.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-64 overflow-y-auto">
              {stuckOrders.map(order => (
                <div key={order.id} className="bg-white rounded-lg p-4 border border-amber-200">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-gray-900">{order.order_number}</span>
                    <span className="text-xs text-red-600 font-medium">{getMinutesAgo(order.created_at)}</span>
                  </div>
                  <div className="text-sm text-gray-600 mb-1">
                    {order.user_profile?.name || order.user_profile?.email || 'Unknown customer'}
                  </div>
                  <div className="text-sm font-medium text-green-600 mb-3">
                    R{parseFloat(order.total).toFixed(2)}
                  </div>
                  <button
                    onClick={() => cancelStuckOrder(order.id, order.order_number)}
                    className="w-full px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    Cancel Order
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Gauges */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Completion Rate</h3>
            <GaugeComponent
              type="radial"
              arc={{
                colorArray: ['#ef4444', '#f59e0b', '#10b981'],
                padding: 0.02,
                subArcs: [
                  { limit: 50 },
                  { limit: 75 },
                  { limit: 100 }
                ]
              }}
              pointer={{ type: "needle", animationDelay: 0 }}
              value={metrics.completionRate}
              minValue={0}
              maxValue={100}
              labels={{
                valueLabel: {
                  style: { fontSize: '35px', fill: '#1f2937' },
                  formatTextValue: value => `${Math.round(value)}%`
                },
                tickLabels: {
                  type: 'inner',
                  ticks: [
                    { value: 0 },
                    { value: 50 },
                    { value: 100 }
                  ]
                }
              }}
            />
            <p className="text-center text-sm text-gray-600 mt-2">
              Orders completed/delivered
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Fulfillment Rate</h3>
            <GaugeComponent
              type="radial"
              arc={{
                colorArray: ['#ef4444', '#f59e0b', '#10b981'],
                padding: 0.02,
                subArcs: [
                  { limit: 70 },
                  { limit: 90 },
                  { limit: 100 }
                ]
              }}
              pointer={{ type: "needle", animationDelay: 0 }}
              value={metrics.fulfillmentRate}
              minValue={0}
              maxValue={100}
              labels={{
                valueLabel: {
                  style: { fontSize: '35px', fill: '#1f2937' },
                  formatTextValue: value => `${Math.round(value)}%`
                },
                tickLabels: {
                  type: 'inner',
                  ticks: [
                    { value: 0 },
                    { value: 50 },
                    { value: 100 }
                  ]
                }
              }}
            />
            <p className="text-center text-sm text-gray-600 mt-2">
              Orders successfully processed
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Order Velocity</h3>
            <div className="flex flex-col items-center justify-center h-64">
              <div className="text-6xl font-bold text-blue-600">
                {metrics.ordersPerHour.toFixed(1)}
              </div>
              <div className="text-xl text-gray-600 mt-2">orders/hour</div>
              <div className="mt-4 text-sm text-gray-500 text-center">
                Avg processing: <span className="font-semibold text-gray-700">
                  {metrics.avgProcessingTime.toFixed(1)}h
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex gap-4 px-6">
              {['overview', 'analytics', 'orders'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Status Overview */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Order Status Distribution</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Object.entries(stats).filter(([key]) => key !== 'total').map(([status, count]) => (
                      <div key={status} className="bg-gray-50 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-gray-900">{count}</div>
                        <div className="text-sm text-gray-600 capitalize mt-1">
                          {status.replace('_', ' ')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Revenue by Status */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Revenue by Status</h3>
                    {revenueByStatus.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={revenueByStatus}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ status, percent }) => `${status}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="revenue"
                          >
                            {revenueByStatus.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-500">No data available</p>
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">Payment Methods</h3>
                    {paymentMethodBreakdown.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={paymentMethodBreakdown}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="method" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#3b82f6" name="Orders" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-gray-500">No data available</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-4">Hourly Order Volume & Revenue</h3>
                  {hourlyOrders.length > 0 ? (
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={hourlyOrders}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" />
                        <YAxis yAxisId="left" label={{ value: 'Orders', angle: -90, position: 'insideLeft' }} />
                        <YAxis yAxisId="right" orientation="right" label={{ value: 'Revenue (R)', angle: 90, position: 'insideRight' }} />
                        <Tooltip />
                        <Legend />
                        <Area yAxisId="left" type="monotone" dataKey="orders" fill="#3b82f6" stroke="#3b82f6" fillOpacity={0.6} name="Orders" />
                        <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Revenue (R)" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500">No hourly data available</p>
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Order Value Distribution</h3>
                  {orderValueDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={orderValueDistribution}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="range" />
                        <YAxis label={{ value: 'Number of Orders', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#10b981" name="Orders" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500">No distribution data available</p>
                  )}
                </div>

                {processingTimeTrends.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Processing Time Trends</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={processingTimeTrends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Line type="monotone" dataKey="avgTime" stroke="#f59e0b" strokeWidth={2} name="Avg Processing Time (h)" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        placeholder="Search orders, customers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="picking">Picking</option>
                    <option value="packing">Packing</option>
                    <option value="dispatched">Dispatched</option>
                    <option value="delivered">Delivered</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="expired">Expired / Abandoned</option>
                  </select>
                  <button
                    onClick={exportToExcel}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Download size={18} />
                    Export Excel
                  </button>
                </div>

                {/* Orders Table */}
                {filteredOrders.length > 0 ? (
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredOrders.map((order) => (
                            <tr key={order.id} className="hover:bg-gray-50">
                              <td className="px-3 py-4 whitespace-nowrap">
                                <span className="font-medium text-blue-600">{order.order_number}</span>
                              </td>
                              <td className="px-3 py-4 max-w-[220px]">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {order.user_profiles?.name || 'Unknown'}
                                </div>
                                <div className="text-xs text-gray-500 truncate">
                                  {order.user_profiles?.email}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {order.user_profiles?.phone}
                                </div>
                                {order.delivery_addresses && (
                                  <div className="text-xs text-gray-400 mt-1 break-words">
                                    {order.delivery_addresses.physical_address}
                                    {order.delivery_addresses.complex_name && `, ${order.delivery_addresses.complex_name}`}
                                    {order.delivery_addresses.unit_number && ` (${order.delivery_addresses.unit_number})`}
                                    {order.delivery_addresses.area && ` - ${order.delivery_addresses.area}`}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap">
                                <div className="font-medium text-gray-900">
                                  {formatCurrency(order.total)}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {order.order_items?.length || 0} items
                                </div>
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                                  {order.status?.toUpperCase()}
                                </span>
                              </td>
                              {/* Capped and stacked: the badge stays on one line, the bank's
                                  reason wraps beneath it. Without the cap a long reason
                                  ("SGS-050051: Insufficient funds") widened the column until
                                  Actions was pushed off the right edge. */}
                              <td className="px-3 py-4 align-top" style={{ maxWidth: 190 }}>
                                {(() => {
                                  const pstate = getPaymentState(order);
                                  return (
                                    <div className="flex flex-col items-start gap-1">
                                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${pstate.color}`}>
                                        {pstate.label}
                                      </span>
                                      {pstate.reason && (
                                        <span
                                          className="text-[11px] leading-tight text-gray-500 break-words"
                                          title={pstate.reason}
                                        >
                                          {pstate.reason}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-600">
                                {formatDate(order.created_at)}
                              </td>
                              <td className="px-3 py-4 whitespace-nowrap text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => handleViewDetails(order)}
                                    className="text-blue-600 hover:text-blue-800"
                                    title="View Details"
                                  >
                                    <Eye size={18} />
                                  </button>
                                  {order.status === 'pending' && (
                                    <button
                                      onClick={() => {
                                        setOrderToDelete(order);
                                        setShowDeleteModal(true);
                                      }}
                                      className="text-red-600 hover:text-red-800"
                                      title="Delete Order"
                                    >
                                      <Trash2 size={18} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <ShoppingCart size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No orders found</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
              <p className="mt-4 text-gray-700">Loading dashboard...</p>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && orderToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} className="text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 text-center mb-2">Delete Order?</h2>
              <p className="text-gray-600 text-center mb-2">
                Are you sure you want to delete order <span className="font-bold">#{orderToDelete.order_number}</span>?
              </p>
              <p className="text-sm text-gray-500 text-center mb-6">
                This will permanently delete the order and all its items. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setOrderToDelete(null);
                  }}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrder}
                  disabled={deleting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : (
                    'Delete Order'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
                <p className="text-sm text-gray-600">Order #{selectedOrder.order_number}</p>
              </div>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedOrder(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Update Messages - Only for owners */}
              {isOwner && statusUpdateError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <XCircle size={18} />
                  {statusUpdateError}
                </div>
              )}
              {isOwner && statusUpdateSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                  <CheckCircle size={18} />
                  {statusUpdateSuccess}
                </div>
              )}

              {/* Order Status & Payment - Editable for owners only */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Package size={18} />
                    Order Status
                    {isOwner && <Edit size={14} className="text-gray-400" />}
                  </h3>
                  {isOwner ? (
                    <select
                      value={selectedOrder.status || 'pending'}
                      onChange={(e) => updateOrderStatus(selectedOrder.id, e.target.value)}
                      disabled={updatingStatus}
                      className={`w-full px-3 py-2 text-sm font-medium rounded-lg border-2 border-transparent 
                        focus:outline-none focus:border-blue-400 cursor-pointer transition-all
                        ${updatingStatus ? 'opacity-50 cursor-wait' : ''}
                        ${ORDER_STATUSES.find(s => s.value === selectedOrder.status)?.color || 'bg-gray-100 text-gray-700'}`}
                    >
                      {ORDER_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${ORDER_STATUSES.find(s => s.value === selectedOrder.status)?.color || 'bg-gray-100 text-gray-700'}`}>
                      {selectedOrder.status?.toUpperCase()}
                    </span>
                  )}
                </div>
                
                <div className="bg-green-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <CreditCard size={18} />
                    Payment Status
                    {isOwner && <Edit size={14} className="text-gray-400" />}
                  </h3>
                  {isOwner ? (
                    <select
                      value={selectedOrder.payment_status || 'pending'}
                      onChange={(e) => updateOrderStatus(selectedOrder.id, selectedOrder.status, e.target.value)}
                      disabled={updatingStatus}
                      className={`w-full px-3 py-2 text-sm font-medium rounded-lg border-2 border-transparent 
                        focus:outline-none focus:border-green-400 cursor-pointer transition-all
                        ${updatingStatus ? 'opacity-50 cursor-wait' : ''}
                        ${PAYMENT_STATUSES.find(s => s.value === selectedOrder.payment_status)?.color || 'bg-yellow-100 text-yellow-700'}`}
                    >
                      {PAYMENT_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                      selectedOrder.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {selectedOrder.payment_status?.toUpperCase() || 'PENDING'}
                    </span>
                  )}
                  <div className="mt-2 text-xs text-gray-600">
                    Method: {selectedOrder.payment_method || 'Not specified'}
                  </div>
                  {selectedOrder.paid_at && (
                    <div className="text-xs text-gray-600">
                      Paid: {formatDate(selectedOrder.paid_at)}
                    </div>
                  )}
                </div>
              </div>

              {/* ============================================
                  🔍 BIDVEST TRANSACTION TRACKER
                  Our payment_status is a claim. This is the gateway's truth.
                  ============================================ */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <CreditCard size={18} />
                    BidVest Transaction Tracker
                  </h3>
                  <button
                    onClick={() => checkBidvest(selectedOrder)}
                    disabled={bidvestChecking}
                    className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                      bidvestChecking
                        ? 'bg-gray-200 text-gray-500 cursor-wait'
                        : 'bg-slate-800 text-white hover:bg-slate-700'
                    }`}
                  >
                    {bidvestChecking ? 'Checking BidVest…' : 'Check BidVest'}
                  </button>
                </div>

                {!bidvestCheck && !bidvestChecking && (
                  <p className="text-xs text-gray-500">
                    Ask BidVest what actually happened to this order. Read-only — it never
                    changes the order or takes money.
                  </p>
                )}

                {bidvestCheck?.error && (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                    Could not reach BidVest: {bidvestCheck.error}
                  </div>
                )}

                {bidvestCheck && !bidvestCheck.error && (() => {
                  const says = bidvestCheck.bidvest_says || bidvestCheck.outcome;
                  const ourPaid = selectedOrder.payment_status === 'paid';
                  const theirPaid = says === 'paid';
                  const match = ourPaid === theirPaid;
                  const verdict = match
                    ? { text: '✅ MATCH — our records agree with BidVest', cls: 'bg-green-50 border-green-200 text-green-800' }
                    : theirPaid
                      // The dangerous one: money taken, order not released.
                      ? { text: '🚨 MISMATCH — BIDVEST HAS THE MONEY BUT WE HAVE NOT CONFIRMED THIS ORDER', cls: 'bg-red-50 border-red-300 text-red-800' }
                      // Equally dangerous: we think it is paid and it is not.
                      : { text: '🚨 MISMATCH — WE SAY PAID BUT BIDVEST HAS NO SUCCESSFUL PAYMENT', cls: 'bg-red-50 border-red-300 text-red-800' };

                  const label = {
                    paid: 'PAID — money received',
                    declined: 'DECLINED — the bank refused it',
                    no_attempt: 'NO ATTEMPT — customer never paid',
                    amount_mismatch: 'AMOUNT MISMATCH — paid a different amount',
                    cancelled: 'CANCELLED',
                    not_found: 'ORDER NOT FOUND',
                  }[says] || String(says || 'unknown').toUpperCase();

                  return (
                    <div className="space-y-3">
                      <div className={`rounded-lg border p-3 text-sm font-semibold ${verdict.cls}`}>
                        {verdict.text}
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-white rounded-lg border border-gray-200 p-3">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">Our side</div>
                          <div className="font-semibold text-gray-900">
                            {(selectedOrder.payment_status || 'pending').toUpperCase()}
                          </div>
                          <div className="text-xs text-gray-600 mt-1">status: {selectedOrder.status}</div>
                          <div className="text-xs text-gray-600">due: R{selectedOrder.amount_due ?? selectedOrder.total}</div>
                        </div>
                        <div className="bg-white rounded-lg border border-gray-200 p-3">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">BidVest says</div>
                          <div className="font-semibold text-gray-900">{label}</div>
                          {bidvestCheck.charged_total != null && (
                            <div className="text-xs text-gray-600 mt-1">charged: R{bidvestCheck.charged_total}</div>
                          )}
                          {bidvestCheck.successful_count != null && (
                            <div className="text-xs text-gray-600">successful charges: {bidvestCheck.successful_count}</div>
                          )}
                        </div>
                      </div>

                      {bidvestCheck.decline_reason && (
                        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <span className="font-semibold text-amber-900">Reason from the bank: </span>
                          <span className="text-amber-800">{bidvestCheck.decline_reason}</span>
                        </div>
                      )}

                      {bidvestCheck.overcharged && (
                        <div className="text-sm bg-red-50 border border-red-300 rounded-lg p-3 font-semibold text-red-800">
                          🚨 CUSTOMER CHARGED MORE THAN ONCE — refund the extra
                        </div>
                      )}

                      {!!bidvestCheck.transactions?.length && (
                        <div className="bg-white rounded-lg border border-gray-200 p-3">
                          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                            Gateway transactions (use these to refund)
                          </div>
                          <div className="space-y-1">
                            {bidvestCheck.transactions.map((t, i) => (
                              <div key={i} className="font-mono text-xs text-gray-700 flex flex-wrap gap-x-3">
                                <span>id {t.id}</span>
                                <span>R{t.amount}</span>
                                <span>{t.status}</span>
                                {t.gateway_reference && <span>ref {t.gateway_reference}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!match && theirPaid && (
                        <p className="text-xs text-gray-600">
                          The money is there. Use Payment Status → Paid to release it — that now
                          re-checks BidVest before writing, so it will confirm cleanly.
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Quick Actions - Only for owners */}
              {isOwner && (
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Activity size={18} />
                  Quick Actions
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'confirmed', 'paid')}
                    disabled={updatingStatus || (selectedOrder.status === 'confirmed' && selectedOrder.payment_status === 'paid')}
                    className="px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <CheckCircle size={14} />
                    Confirm & Mark Paid
                  </button>
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'packed')}
                    disabled={updatingStatus || selectedOrder.status === 'packed'}
                    className="px-3 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Package size={14} />
                    Mark Packed
                  </button>
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'delivered')}
                    disabled={updatingStatus || selectedOrder.status === 'delivered'}
                    className="px-3 py-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <Truck size={14} />
                    Mark Delivered
                  </button>
                  <button
                    onClick={() => updateOrderStatus(selectedOrder.id, 'cancelled')}
                    disabled={updatingStatus || selectedOrder.status === 'cancelled'}
                    className="px-3 py-2 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <XCircle size={14} />
                    Cancel Order
                  </button>
                </div>
              </div>
              )}

              {/* Customer Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <User size={18} />
                  Customer Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {selectedOrder.user_profiles?.name || 'Unknown'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {selectedOrder.user_profiles?.email || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Phone:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {selectedOrder.user_profiles?.phone || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Delivery Time:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {selectedOrder.delivery_time || 'ASAP'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Delivery Address */}
              {selectedOrder.delivery_addresses && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin size={18} />
                    Delivery Address
                  </h3>
                  <div className="text-sm text-gray-900">
                    <div>{selectedOrder.delivery_addresses.street_address}</div>
                    <div>{selectedOrder.delivery_addresses.suburb}</div>
                    <div>{selectedOrder.delivery_addresses.city}, {selectedOrder.delivery_addresses.postal_code}</div>
                    {selectedOrder.delivery_addresses.special_instructions && (
                      <div className="mt-2 text-gray-600">
                        <span className="font-medium">Instructions:</span> {selectedOrder.delivery_addresses.special_instructions}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Order Items */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Package size={18} />
                  Order Items ({selectedOrder.order_items?.length || 0})
                </h3>
                <div className="space-y-3">
                  {selectedOrder.order_items?.map((item) => {
                    const hasPromo = item.promo_price !== null && item.promo_price !== undefined;
                    const regularTotal = item.price * item.quantity;
                    const effectiveTotal = hasPromo ? (item.subtotal || item.promo_price * item.quantity) : regularTotal;
                    
                    return (
                      <div key={item.id} className={`flex items-center gap-3 p-3 rounded-lg ${hasPromo ? 'bg-green-50 border border-green-200' : 'bg-white'}`}>
                        {item.products?.image && (
                          <img
                            src={item.products.image}
                            alt={item.products.name}
                            className="w-16 h-16 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{item.products?.name}</div>
                          <div className="text-sm text-gray-600">
                            Qty: {item.quantity}
                            {item.picked_quantity !== null && item.picked_quantity !== item.quantity && (
                              <span className="ml-2 text-orange-600">
                                (Picked: {item.picked_quantity})
                              </span>
                            )}
                          </div>
                          {hasPromo && item.promotion_type && (
                            <div className="text-xs text-green-700 font-medium mt-1">
                              {(() => {
                                if (item.promotion_type !== 'all_in_one' && item.promotion_type !== 'combo') {
                                  return `Markdown: ${formatCurrency(item.promo_price)}/ea`;
                                }
                                // Combo label must show the BUNDLE size (e.g. "3 for R100"),
                                // not the ordered quantity. promo_price is the price per bundle
                                // and subtotal is bundles x promo_price, so the number of bundles
                                // is subtotal / promo_price and the bundle size is quantity /
                                // bundles. e.g. qty 6, promo_price 100, subtotal 200 -> 2 bundles
                                // -> "3 for R100". Previously it printed "6 for R100", making it
                                // look like 6 units cost R100.
                                const bundlePrice = Number(item.promo_price) || 0;
                                const total = Number(item.subtotal ?? item.promo_price * item.quantity);
                                const bundles = bundlePrice > 0 ? Math.round(total / bundlePrice) : 0;
                                const bundleQty = bundles > 0 && item.quantity % bundles === 0
                                  ? item.quantity / bundles
                                  : null;
                                return bundleQty
                                  ? `${bundleQty} for ${formatCurrency(bundlePrice)}`
                                  : `Combo: ${formatCurrency(bundlePrice)}`;
                              })()}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          {hasPromo ? (
                            <>
                              <div className="text-xs text-gray-400 line-through">
                                {formatCurrency(regularTotal)}
                              </div>
                              <div className="font-medium text-green-700">
                                {formatCurrency(effectiveTotal)}
                              </div>
                              <div className="text-xs text-gray-500">
                                was {formatCurrency(item.price)}/ea
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="font-medium text-gray-900">
                                {formatCurrency(regularTotal)}
                              </div>
                              <div className="text-xs text-gray-600">
                                {formatCurrency(item.price)}/ea
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Order Totals */}
                {(() => {
                  const grossTotal = (selectedOrder.order_items || []).reduce((sum, item) => sum + (item.price * item.quantity), 0);
                  const itemsTotal = (selectedOrder.order_items || []).reduce((sum, item) => {
                    if (item.promo_price !== null && item.promo_price !== undefined) {
                      return sum + (item.subtotal || item.promo_price * item.quantity);
                    }
                    return sum + (item.price * item.quantity);
                  }, 0);
                  const totalDiscount = grossTotal - itemsTotal;
                  const deliveryFee = parseFloat(selectedOrder.delivery_fee) || 0;
                  const plasticBagFee = parseFloat(selectedOrder.plastic_bag_fee) || 0;
                  const walletUsed = parseFloat(selectedOrder.wallet_credit_used) || 0;
                  const nett = parseFloat(selectedOrder.total) || (itemsTotal + deliveryFee + plasticBagFee);

                  return (
                    <div className="mt-4 pt-4 border-t border-gray-300">
                      <div className="space-y-2 max-w-xs ml-auto">
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Gross</span>
                          <span>{formatCurrency(grossTotal)}</span>
                        </div>
                        {totalDiscount > 0 && (
                          <div className="flex justify-between text-sm text-green-600">
                            <span>Less Discounts</span>
                            <span>-{formatCurrency(totalDiscount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm font-semibold text-gray-900 border-t border-gray-200 pt-2">
                          <span>Subtotal</span>
                          <span>{formatCurrency(itemsTotal)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Delivery</span>
                          <span>{formatCurrency(deliveryFee + plasticBagFee)}</span>
                        </div>
                        {walletUsed > 0 && (
                          <div className="flex justify-between text-sm text-blue-600">
                            <span>Wallet Credit</span>
                            <span>-{formatCurrency(walletUsed)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-base font-bold text-gray-900 border-t-2 border-gray-900 pt-2">
                          <span>Nett</span>
                          <span>{formatCurrency(nett)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Operations Status */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Operations Status</h3>
                
                {/* Order Confirmed */}
                {(() => {
                  const timestamp = selectedOrder.status_history?.confirmed || selectedOrder.created_at;
                  const isComplete = timestamp && selectedOrder.status !== 'pending' && selectedOrder.status !== 'cancelled';
                  return (
                    <div className="mb-2 text-sm flex items-center flex-wrap gap-2">
                      <span className="font-medium w-20">Order:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        selectedOrder.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        isComplete ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {selectedOrder.status === 'cancelled' ? 'cancelled' : isComplete ? 'confirmed' : 'pending'}
                      </span>
                      {timestamp && (
                        <span className="text-gray-500 text-xs">
                          {new Date(timestamp).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Payment */}
                {(() => {
                  const timestamp = selectedOrder.paid_at;
                  const isComplete = selectedOrder.payment_status === 'paid';
                  return (
                    <div className="mb-2 text-sm flex items-center flex-wrap gap-2">
                      <span className="font-medium w-20">Payment:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        isComplete ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {isComplete ? 'paid' : 'pending'}
                      </span>
                      {selectedOrder.payment_method && (
                        <span className="text-gray-500 text-xs">({selectedOrder.payment_method})</span>
                      )}
                      {timestamp && (
                        <span className="text-gray-500 text-xs">
                          {new Date(timestamp).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Picking */}
                {(() => {
                  const startedAt = selectedOrder.status_history?.picking;
                  const completedAt = selectedOrder.status_history?.picking_complete;
                  const isComplete = !!completedAt;
                  const isInProgress = startedAt && !completedAt;
                  const shouldShow = startedAt || ['picking', 'picking_complete', 'packing', 'packed', 'ready_for_dispatch', 'out_for_delivery', 'delivered'].includes(selectedOrder.status);
                  
                  if (!shouldShow) return null;
                  return (
                    <div className="mb-2 text-sm flex items-center flex-wrap gap-2">
                      <span className="font-medium w-20">Picking:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        isComplete ? 'bg-green-100 text-green-700' : 
                        isInProgress ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {isComplete ? 'completed' : isInProgress ? 'in progress' : 'pending'}
                      </span>
                      {(completedAt || startedAt) && (
                        <span className="text-gray-500 text-xs">
                          {new Date(completedAt || startedAt).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Packing */}
                {(() => {
                  const startedAt = selectedOrder.status_history?.packing;
                  const completedAt = selectedOrder.status_history?.packed;
                  const isComplete = !!completedAt;
                  const isInProgress = startedAt && !completedAt;
                  const shouldShow = startedAt || ['packing', 'packed', 'ready_for_dispatch', 'out_for_delivery', 'delivered'].includes(selectedOrder.status);
                  
                  if (!shouldShow) return null;
                  return (
                    <div className="mb-2 text-sm flex items-center flex-wrap gap-2">
                      <span className="font-medium w-20">Packing:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        isComplete ? 'bg-green-100 text-green-700' : 
                        isInProgress ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {isComplete ? 'completed' : isInProgress ? 'in progress' : 'pending'}
                      </span>
                      {(completedAt || startedAt) && (
                        <span className="text-gray-500 text-xs">
                          {new Date(completedAt || startedAt).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Box Allocation */}
                {(() => {
                  const timestamp = selectedOrder.status_history?.ready_for_dispatch;
                  const boxAlloc = selectedOrder.box_allocation;
                  const isComplete = !!timestamp || !!boxAlloc;
                  const shouldShow = timestamp || boxAlloc || ['packed', 'ready_for_dispatch', 'out_for_delivery', 'delivered'].includes(selectedOrder.status);
                  
                  if (!shouldShow) return null;
                  return (
                    <div className="mb-2 text-sm flex items-center flex-wrap gap-2">
                      <span className="font-medium w-20">Box:</span>
                      {boxAlloc?.delivery_boxes?.box_number && (
                        <span className="font-mono">Box {boxAlloc.delivery_boxes.box_number}</span>
                      )}
                      {boxAlloc?.delivery_boxes?.current_zone && (
                        <span className="text-purple-600 text-xs">({boxAlloc.delivery_boxes.current_zone})</span>
                      )}
                      <span className={`px-2 py-1 rounded text-xs ${
                        isComplete ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {isComplete ? 'allocated' : 'pending'}
                      </span>
                      {timestamp && (
                        <span className="text-gray-500 text-xs">
                          {new Date(timestamp).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Driver */}
                {(() => {
                  const driver = selectedOrder.driver;
                  const shouldShow = driver || ['ready_for_dispatch', 'out_for_delivery', 'delivered'].includes(selectedOrder.status);
                  
                  if (!shouldShow) return null;
                  return (
                    <div className="mb-2 text-sm flex items-center flex-wrap gap-2">
                      <span className="font-medium w-20">Driver:</span>
                      {driver?.name ? (
                        <>
                          <span>{driver.name}</span>
                          {driver.phone && <span className="text-gray-500 text-xs">({driver.phone})</span>}
                        </>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-700">not assigned</span>
                      )}
                    </div>
                  );
                })()}

                {/* Collection */}
                {(() => {
                  const timestamp = selectedOrder.status_history?.out_for_delivery;
                  const isComplete = !!timestamp || ['out_for_delivery', 'delivered'].includes(selectedOrder.status);
                  const shouldShow = timestamp || selectedOrder.driver || ['ready_for_dispatch', 'out_for_delivery', 'delivered'].includes(selectedOrder.status);
                  
                  if (!shouldShow) return null;
                  return (
                    <div className="mb-2 text-sm flex items-center flex-wrap gap-2">
                      <span className="font-medium w-20">Collection:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        isComplete ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {isComplete ? 'collected' : 'pending'}
                      </span>
                      {timestamp && (
                        <span className="text-gray-500 text-xs">
                          {new Date(timestamp).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  );
                })()}

                {/* Delivery */}
                {(() => {
                  const deliveredAt = selectedOrder.status_history?.delivered;
                  const outForDeliveryAt = selectedOrder.status_history?.out_for_delivery;
                  const isDelivered = !!deliveredAt || selectedOrder.status === 'delivered';
                  const isInTransit = !isDelivered && (outForDeliveryAt || selectedOrder.status === 'out_for_delivery');
                  const shouldShow = deliveredAt || outForDeliveryAt || ['out_for_delivery', 'delivered'].includes(selectedOrder.status);
                  
                  if (!shouldShow) return null;
                  return (
                    <div className="mb-2 text-sm flex items-center flex-wrap gap-2">
                      <span className="font-medium w-20">Delivery:</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        isDelivered ? 'bg-green-100 text-green-700' : 
                        isInTransit ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {isDelivered ? 'delivered' : isInTransit ? 'in transit' : 'pending'}
                      </span>
                      {isDelivered && deliveredAt && (
                        <span className="text-gray-500 text-xs">
                          {new Date(deliveredAt).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      {isInTransit && outForDeliveryAt && (
                        <span className="text-gray-500 text-xs">
                          Started: {new Date(outForDeliveryAt).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <DollarSign size={18} />
                  Order Summary
                </h3>
                <div className="space-y-2 text-sm">
                  {(() => {
                    const grossTotal = (selectedOrder.order_items || []).reduce((sum, item) => sum + (item.price * item.quantity), 0);
                    const itemsTotal = (selectedOrder.order_items || []).reduce((sum, item) => {
                      if (item.promo_price !== null && item.promo_price !== undefined) {
                        return sum + (parseFloat(item.subtotal) || item.promo_price * item.quantity);
                      }
                      return sum + (item.price * item.quantity);
                    }, 0);
                    const totalDiscount = grossTotal - itemsTotal;
                    const deliveryFee = parseFloat(selectedOrder.delivery_fee) || 0;
                    const plasticBagFee = parseFloat(selectedOrder.plastic_bag_fee) || 0;
                    const walletUsed = parseFloat(selectedOrder.wallet_credit_used) || 0;
                    const nett = parseFloat(selectedOrder.total) || (itemsTotal + deliveryFee + plasticBagFee);

                    return (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Gross:</span>
                          <span className="font-medium text-gray-900">{formatCurrency(grossTotal)}</span>
                        </div>
                        {totalDiscount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-green-600">Less Discounts:</span>
                            <span className="font-medium text-green-600">-{formatCurrency(totalDiscount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t border-gray-200 pt-2">
                          <span className="font-semibold text-gray-900">Subtotal:</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(itemsTotal)}</span>
                        </div>
                        {(deliveryFee + plasticBagFee) > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Delivery:</span>
                            <span className="font-medium text-gray-900">{formatCurrency(deliveryFee + plasticBagFee)}</span>
                          </div>
                        )}
                        {walletUsed > 0 && (
                          <div className="flex justify-between">
                            <span className="text-blue-600">Wallet Credit:</span>
                            <span className="font-medium text-blue-600">-{formatCurrency(walletUsed)}</span>
                          </div>
                        )}
                        <div className="border-t-2 border-gray-900 pt-2 flex justify-between">
                          <span className="font-bold text-gray-900">Nett:</span>
                          <span className="font-bold text-gray-900 text-lg">{formatCurrency(nett)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Notes */}
              {selectedOrder.notes && (
                <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <AlertCircle size={18} className="text-yellow-600" />
                    Order Notes
                  </h3>
                  <p className="text-sm text-gray-700">{selectedOrder.notes}</p>
                </div>
              )}

              {/* Timestamps */}
              <div className="bg-gray-50 rounded-xl p-4 text-xs text-gray-600">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar size={14} />
                  <span className="font-medium">Created:</span>
                  <span>{formatDate(selectedOrder.created_at)}</span>
                </div>
                {selectedOrder.paid_at && (
                  <div className="flex items-center gap-2">
                    <Clock size={14} />
                    <span className="font-medium">Paid:</span>
                    <span>{formatDate(selectedOrder.paid_at)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600'
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDashboard;
